import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuditEventDTO } from '../tokens/master-admin-audit.token';
import { OverrideLogFilter, OverrideLogService } from '../tokens/override-log.token';
import { API_PATHS } from '../config/api-paths';

/**
 * REST-backed {@link OverrideLogService} (EM-628 / S0-8).
 *
 * GET /services/embedded/api/master-admin/audit/override-log        → AuditEventDTO[] (+ X-Total-Count)
 * GET /services/embedded/api/master-admin/audit/override-log/export → text/csv Blob
 *
 * Rule 07 / Rule 10 — REST-only, no mock fallback. Reuses the audit read path (Rule 17): the backend
 * pins eventCategory=COMPLIANCE + eventType LIKE 'SM_OVERRIDE_%' so this service only narrows by
 * actor / date.
 */
@Injectable()
export class OverrideLogRestService implements OverrideLogService {
  private readonly http = inject(HttpClient);

  private get base(): string {
    return `${API_PATHS.EMBEDDED_API_BASE}/master-admin/audit/override-log`;
  }

  search(
    filter: OverrideLogFilter,
    page: number,
    size: number,
  ): Observable<HttpResponse<AuditEventDTO[]>> {
    return this.http.get<AuditEventDTO[]>(this.base, {
      params: this.buildParams(filter, page, size),
      observe: 'response',
    });
  }

  exportCsv(filter: OverrideLogFilter): Observable<Blob> {
    return this.http.get(`${this.base}/export`, {
      params: this.buildFilterParams(filter),
      responseType: 'blob',
    });
  }

  private buildParams(filter: OverrideLogFilter, page: number, size: number): HttpParams {
    let p = this.buildFilterParams(filter);
    p = p.set('page', String(page));
    p = p.set('size', String(size));
    p = p.set('sort', 'occurredAt,desc');
    return p;
  }

  private buildFilterParams(filter: OverrideLogFilter): HttpParams {
    let p = new HttpParams();
    if (filter.actorLogin?.trim()) p = p.set('actorLogin', filter.actorLogin.trim());
    if (filter.from?.trim()) p = p.set('from', filter.from.trim());
    if (filter.to?.trim()) p = p.set('to', filter.to.trim());
    return p;
  }
}
